import api from "../../api/api"
import { accessToOperation } from "../../plugins/authorized"
import router from "../../router"

const state = () => ({
	status: "",
	token: "" /* Will be used when switching over to JWT instead of cookie */,
	refreshToken: "",
	user: {},
	error: null,
	errorCode: null
})

// getters
const getters = {
	isLoggedIn: state => !!state.token && !!state.user,
	authStatus: state => state.status,
	getUser: state => state.user,
	getToken: state => state.token,
	forcePasswordReset: state => !!state.user.forcePasswordReset,
	isGuest: state => !!state.user.isGuest,
}

// actions
const actions = {
	login({ commit }, user) {
		return new Promise((resolve, reject) => {
			commit("auth_request")

			if (!user || !user.username || !user.password) {
				commit("auth_error", {error: "Please input username and password.", code: null})
			}
			api.post(`/auth/token`, {
				username: user.username,
				password: user.password,
				resendVerification: user.resendVerification,
				captchaToken: user.captchaToken,
			}, { nointercept: true })
				.then(value => {
					if (!value.data || !value.data.error || !value.data.data) {
						throw new Error("Could not get response!")
					}
					if (value.data.error.length > 0) {
						const apiErr = value.data.error[0]
						commit("auth_error", {error: apiErr.message, code: apiErr.code})
						commit("reset_token")
						console.debug("Login was rejected, logging out.")
						return reject(apiErr)
					}
					let data = value.data.data[0]
					var token = data.token
					var refreshToken = data.refreshToken
					var usr = data.user
					api.defaults.headers.common["Authorization"] = token
					commit("auth_success", { token, user: usr, refreshToken })
					if (!accessToOperation("POR", "R")) {
						commit("logout")
						reject()
						commit("auth_error", {error: "User is inactive", code: null})
						router.push("/login")
						return
					}
					resolve()
				})
				.catch(err => {
					commit("auth_error", err)
					commit("reset_token")
					console.log("Login was rejected")
					reject()
				})
		})
	},
	resetError({commit}) {
		commit("reset_error")
	},
	refreshToken({ commit, state }) {
		return new Promise((resolve, reject) => {
			commit("auth_request")

			api.post(`/auth/token/refresh`, {refreshToken: state.refreshToken}, {refreshTokenRequest: true})
				.then(value => {
					if (!value.data || !value.data.error || !value.data.data) {
						throw new Error("Could not get response!")
					}
					if (value.data.error.length > 0) {
						commit("auth_error", {error: value.data.error[0].message, code: value.data.error[0].code})
						commit("reset_token")
						console.log("Login was rejected")
						return reject()
					}
					let data = value.data.data[0]
					var token = data.token
					var usr = data.user
					var refreshToken = data.refreshToken
					api.defaults.headers.common["Authorization"] = token
					console.log("Token set.")
					commit("auth_success", { token, user: usr, refreshToken  })
					if (!accessToOperation("POR", "R")) {
						commit("logout")
						reject()
						commit("auth_error", {error: "User is inactive", code: null})
						router.push("/login")
						return
					}
					resolve()
				})
				.catch(err => {
					commit("auth_error", {error: err, code: null})
					commit("reset_token")
					console.log("Login was rejected")
					router.push("/login")
					reject()
				})
		})
	},
	logout({ commit }) {
		delete api.defaults.headers.common["Authorization"]
		commit("logout")
	},
	logoutRequest({ commit }) {
		return new Promise((resolve) => {
			delete api.defaults.headers.common["Authorization"]
			commit("logout")
			router.push("/login")
			api.get(`/users/logout`)
			resolve()
		})
	}, 
	verifyLogin({ commit }) {
		return new Promise((resolve, reject) => {

			api.get(`/users/current`)
				.then(value => {
					if (!value.data || !value.data.error || !value.data.data) {
						throw new Error("Could not get response!")
					}
					if (value.data.error.length > 0) {
						commit("logout")
						return reject()
					}
					commit("update_user", {user: value.data.data[0].user})
					if (!accessToOperation("POR", "R")) {
						commit("logout")
						commit("auth_error", {error: "User is inactive", code: null})
						router.push("/login")
						reject()
						return
					}
					resolve()
				})
				.catch(() => {
					commit("logout")
					reject()
				})
		})
	},
	forgotPassword({ commit }, { email }) {
		return new Promise((resolve, reject) => {

			if (!email) {
				commit("set_error", {error: "Please input an email address.", code: null})
			}
			api.post(`/users/forgot-password`, { email }, { nointercept: true })
				.then(value => {
					if (!value.data || !value.data.error || !value.data.data) {
						throw new Error("Could not get response!")
					}
					if (value.data.error.length > 0) {
						commit("set_error", {error: value.data.error[0].message, code: value.data.error[0].code})
						return reject()
					}
					return resolve()
				})
				.catch(err => {
					commit("set_error", {error: err, code: null})
					reject()
				})
		})
	},
	resetPassword({ commit }, { token, password }) {
		return new Promise((resolve, reject) => {

			if (!token || !password) {
				commit("set_error", {error: "Please provide token and new password", code: null})
			}
			api.post(`/users/reset-password`, { token, password })
				.then(value => {
					if (!value.data || !value.data.error || !value.data.data) {
						throw new Error("Could not get response!")
					}
					if (value.data.error.length > 0) {
						const apiErr = value.data.error[0]
						commit("set_error", {error: apiErr.message, code: apiErr.code})
						return reject(apiErr)
					}
					return resolve()
				})
				.catch(err => {
					commit("set_error", {error: err, code: null})
					reject(err)
				})
		})
	},
	verifyResetPasswordToken({ commit }, { token }) {
		return new Promise((resolve, reject) => {

			if (!token) {
				commit("set_error", {error: "Please provide token", code: null})
			}
			api.get(`/users/reset-password/${token}`)
				.then(value => {
					if (!value.data || !value.data.error || !value.data.data) {
						throw new Error("Could not get response!")
					}
					if (value.data.error.length > 0) {
						commit("set_error", {error: value.data.error[0].message, code: value.data.error[0].code})
						return reject()
					}
					if (value.data.data[0] == true) {
						return resolve()
					} else {
						return reject()
					}
				})
				.catch(err => {
					commit("set_error", {error: err, code: null})
					reject()
				})
		})
	},
	verifyEmail({commit}, {token}) {
		return new Promise((resolve, reject) => {
			if (!token) {
				commit("set_error", {error: "No token provided", code: null})
			}
			// Gjør API Kall
			api.post(`/verify/`, { token: token })
				.then((value) => {
					if (value.data.error.length > 0) {
						commit("set_error", {error: value.data.error[0].message, code: value.data.error[0].code})
						return reject(value.data.error[0])
					}
					return resolve()
				})
				.catch((err) => {
					commit("set_error", {error: err, code: null})
					reject()
				})
		})

	}
}

// mutations
const mutations = {
	auth_request(state) {
		state.status = "loading"
	},
	auth_success(state, { token, user, refreshToken }) {
		state.status = "success"
		state.token = token
		state.user = user
		state.refreshToken = refreshToken
		state.error = null
		state.errorCode = null
	},
	update_user(state, { user }) {
		state.status = "success"
		state.user = {
			...state.user,
			...user,
			roles: user.roles || state.user.roles || [],
		}
		state.error = null
		state.errorCode = null
	},
	auth_error(state, {error, code}) {
		state.status = "error"
		state.error = error || "An error occurred"
		state.errorCode = code
	},
	set_error(state, {error, code}) {
		state.error = error || "An error occurred"
		state.errorCode = code
	},
	reset_token(state) {
		state.token = ""
	},
	reset_error(state) {
		state.status = ""
		state.error = null
		state.errorCode = null
	},
	logout(state) {
		state.status = ""
		state.token = ""
		state.user = {}
		state.error = null
		state.errorCode = null
	},
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
}